import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuthContext } from '../../auth/AuthContext';
import MfaSmsForm from '../../components/form/MfaSmsForm/MfaSmsForm';
import PhoneSetupForm from '../../components/form/PhoneSetupForm/PhoneSetupForm';
import SetupAppMFAForm from '../../components/form/SetupAppMFA/SetupAppMFAForm';
import SigninForm from '../../components/form/SigninForm/SigninForm';
import Notification from '../../components/Notification/Notification';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import PreprodTestSiteBanner from '../../components/PreprodTestSiteBanner/PreprodTestSiteBanner';
import config from '../../config';
import content from '../../content';
import { useFormConfig } from '../../hooks/useFormConfig';
import { fetchForm } from '../../service/form';
import type { FetchFormResponse } from '../../service/types';
import { LastLocation } from '../../types';
import { setSelectedFormIdInSession } from '../../utils/form-id';
import EnterAppMFACodeScreen from '../AppMFA/EnterAppMFACodeScreen';
import { isRpaLoginTenant } from '../RpaLoginScreen/rpaLoginConfig';
import SetNewPasswordScreen from '../SetNewPassword/SetNewPasswordScreen';
import type { Page } from './types';

const codes: Record<string, string> = {
  1: content.noticeTokenInvalid,
  2: content.noticeSessionExpired,
  3: content.noticeSessionReplaced,
  4: content.noticeTenantChanged,
};

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const PostLoginLoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.88);
`;

const PostLoginLoadingSpinner = styled.div`
  width: 96px;
  height: 96px;
  border: 10px solid #b1b4b6;
  border-top-color: #1d70b8;
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
  margin: 0 auto;
`;

const PostLoginLoadingLabel = styled.p`
  margin-top: 16px;
  margin-bottom: 0;
  text-align: center;
`;

const getSessionRoleStorageKey = (tenant?: string) =>
  `activeSessionRole:${String(tenant || '').toLowerCase()}`;

const normalizeSessionRoles = (response: FetchFormResponse): string => {
  const roleSet = new Set<string>();
  if (Array.isArray(response?.sessionRoles)) {
    response.sessionRoles.forEach((role) => {
      const normalized = String(role || '').trim().toLowerCase();
      if (normalized) roleSet.add(normalized);
    });
  }
  const responseRole = String(response?.role || '').trim().toLowerCase();
  if (responseRole) roleSet.add(responseRole);
  if (Array.isArray(response?.forms)) {
    response.forms.forEach((form) => {
      const normalized = String(form?.role || '').trim().toLowerCase();
      if (normalized) roleSet.add(normalized);
    });
  }
  return Array.from(roleSet).sort().join('|');
};

export default function SigninScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const loggedOut = searchParams.get('loggedOut') === '1';
  const codeValue = String(searchParams.get('ncode') || '');
  const code = codes[codeValue];
  const notice = codeValue === '4' ? code : loggedOut ? content.noticeLoggedOut : code;
  const [hasRegisterNotice, setHasRegisterNotice] = useState(false);
  const visibleNotice = hasRegisterNotice ? '' : notice;
  const prefilledEmail = (searchParams.get('email') || '').trim().toLowerCase();

  const { isAuthenticated, challengeName, fetchJWT } = useAuthContext();
  const { tenant } = useFormConfig();
  const isLocalGuideLogin = tenant === 'psfa' && searchParams.get('returnTo') === '/psfa/guide';
  const [isPostLoginReady, setIsPostLoginReady] = useState(false);
  const [postLoginTarget, setPostLoginTarget] = useState('');

  const handleRegisterNoticeChange = useCallback(
    (bannerNotice: string) => {
      setHasRegisterNotice(Boolean(bannerNotice));

      if (bannerNotice && searchParams.get('ncode') === '4') {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('ncode');
        setSearchParams(nextSearchParams, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  const landingPath = tenant ? `/${tenant}/${config.urls.landingAccounts}` : `/${config.urls.landingAccounts}`;
  const myFormsPath = tenant ? `/${tenant}?my_forms=1&admin=0` : '/?my_forms=1&admin=0';

  useEffect(() => {
    window.localStorage.removeItem('currentTenant');
  }, []);

  useEffect(() => {
    let cancelled = false;

    const resolvePostLoginForm = async () => {
      if (isLocalGuideLogin) return;
      // Routing invariant: post-login form selection rules are enforced by tests.
      // Run:
      // - screens/SigninScreen/SigninScreen.post-login-routing.test.tsx
      // - components/AccountPage/AccountPage.routing.test.tsx
      // - screens/OverviewScreen/OverviewScreen.routing.test.tsx
      if (!isAuthenticated) {
        if (!cancelled) {
          setIsPostLoginReady(false);
          setPostLoginTarget('');
        }
        return;
      }

      let nextPostLoginTarget = myFormsPath;
      const roleStorageKey = getSessionRoleStorageKey(tenant);
      setSelectedFormIdInSession(tenant, undefined);
      window.sessionStorage.removeItem(roleStorageKey);

      if (isRpaLoginTenant(tenant)) {
        if (!cancelled) {
          setPostLoginTarget(landingPath);
          setIsPostLoginReady(true);
        }
        return;
      }

      try {
        const { idToken } = await fetchJWT({ redirectOnFail: false });
        if (idToken) {
          const response = await fetchForm(idToken, { my_forms: 'true' });
          const normalizedSessionRoleSet = normalizeSessionRoles(response as FetchFormResponse);
          if (normalizedSessionRoleSet) {
            window.sessionStorage.setItem(roleStorageKey, normalizedSessionRoleSet);
          }
          const forms = Array.isArray((response as any)?.forms) ? (response as any).forms : [];

          // Routing rule: if user has any form access records (any role), stay on My Forms.
          if (forms.length > 0) {
            setSelectedFormIdInSession(tenant, undefined);
          } else {
            // Zero forms: always route to landing/new-form flow.
            const createdFormId = String(
              (response as any)?.formId || (response as any)?.form_id || '',
            ).trim();
            if (createdFormId) {
              setSelectedFormIdInSession(tenant, createdFormId);
            } else {
              setSelectedFormIdInSession(tenant, undefined);
            }
            nextPostLoginTarget = landingPath;
          }
        }
      } catch (_error) {
        setSelectedFormIdInSession(tenant, undefined);
        window.sessionStorage.removeItem(roleStorageKey);
      } finally {
        if (!cancelled) {
          setPostLoginTarget(nextPostLoginTarget);
          setIsPostLoginReady(true);
        }
      }
    };

    void resolvePostLoginForm();
    return () => {
      cancelled = true;
    };
  }, [fetchJWT, isAuthenticated, isLocalGuideLogin, landingPath, myFormsPath, tenant]);
  const pages: Page[] = [
    {
      children: (
        <>
          <PreprodTestSiteBanner />
          {visibleNotice && <Notification content={visibleNotice} />}
          <SigninForm
            onSuccess={() => {}}
            prefilledEmail={prefilledEmail}
            onRegisterNoticeChange={handleRegisterNoticeChange}
          />
        </>
      ),
    },
    {
      title: 'Enter your mobile phone number',
      backLink: `/${tenant}/${config.urls.signin}`,
      children: <PhoneSetupForm onSuccess={() => {}} />,
    },
    {
      title: 'Enter the sign in code',
      backLink: `/${tenant}/${config.urls.signin}`,
      children: <MfaSmsForm onSuccess={() => {}} phonenumber="********726" />,
      fullWidth: true,
    },
    {
      title: 'Change your password',
      backLink: `/${tenant}/${config.urls.signin}`,
      children: <SetNewPasswordScreen onSuccess={() => {}} />,
      fullWidth: true,
    },
    {
      title: 'Enter your sign in code',
      backLink: `/${tenant}/${config.urls.signin}`,
      children: <SetupAppMFAForm />,
      fullWidth: true,
    },
    {
      title: 'Enter the sign in code',
      backLink: `/${tenant}/${config.urls.signin}`,
      children: <EnterAppMFACodeScreen />,
    },
  ];

  const activePage = useMemo(() => {
    switch (challengeName) {
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        return pages[5];
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        return pages[3];
      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
        return pages[4];
      default:
        return pages[0];
    }
  }, [challengeName, visibleNotice]);

  if (isAuthenticated) {
    if (isLocalGuideLogin) return <Navigate to="/psfa/guide" replace />;
    if (!isPostLoginReady) {
      return (
        <PageTemplate title="">
          <PostLoginLoadingOverlay role="status" aria-live="polite" aria-label="Loading">
            <div>
              <PostLoginLoadingSpinner />
              <PostLoginLoadingLabel className="govuk-body">Loading...</PostLoginLoadingLabel>
            </div>
          </PostLoginLoadingOverlay>
        </PageTemplate>
      );
    }
    window.sessionStorage.removeItem(LastLocation);
    return <Navigate to={postLoginTarget || myFormsPath} />;
  }

  if (!activePage) {
    window.sessionStorage.removeItem(LastLocation);
    return <Navigate to={landingPath} />;
  }

  return <PageTemplate {...activePage} />;
}
